// One-page buy box: keeps the product form's id/quantity inputs, price, status and gallery in sync
// with the model and bundle radios, entirely client-side (the homepage has no product route to re-render).
// For lots of 2 or 3, one select per item ([data-bundle-pick]) lets the buyer mix models; those selects
// are enabled only while visible, so s-product-form adds one line per chosen model.
class SBuyBox extends HTMLElement {
  #controller;
  #mediaIndex = 0;

  connectedCallback() {
    this.#controller = new AbortController();
    const { signal } = this.#controller;
    this.addEventListener('change', (event) => this.#onChange(event), { signal });
    this.addEventListener('command', (event) => this.#onCommand(event), { signal });
    this.#sync();
  }

  disconnectedCallback() {
    this.#controller?.abort();
  }

  #onChange(event) {
    const target = event.target;
    if (!(target instanceof HTMLInputElement)) return;
    if (target.matches('[data-variant-option]')) {
      // Item 1 mirrors the main model picker.
      const firstPick = this.#pickGroups()[0];
      const mirror = firstPick?.querySelector(`input[type="radio"][value="${CSS.escape(target.value)}"]`);
      if (mirror) mirror.checked = true;
      this.#sync();
    } else if (target.matches('[data-bundle-option]')) {
      this.#sync();
    } else if (target.closest('[data-bundle-pick]')) {
      if (target.closest('[data-bundle-pick]') === this.#pickGroups()[0]) {
        const radio = this.querySelector(`[data-variant-option][value="${CSS.escape(target.value)}"]`);
        if (radio) radio.checked = true;
      }
      this.#sync();
    }
  }

  #pickGroups() {
    return [...this.querySelectorAll('[data-bundle-pick]')];
  }

  #onCommand(event) {
    const source = event.source;
    if (event.command === '--show-media' && source) {
      this.#showMedia(Number(source.dataset.mediaIndex) || 0);
    } else if (event.command === '--next-media') {
      this.#showMedia(this.#mediaIndex + 1);
    } else if (event.command === '--prev-media') {
      this.#showMedia(this.#mediaIndex - 1);
    } else if ((event.command === '--pick-next' || event.command === '--pick-prev') && source) {
      // Scroll a bundle item's thumbnail row by most of its visible width.
      const row = source.closest('[data-bundle-pick]')?.querySelector('[data-pick-row]');
      if (!row) return;
      const rtl = getComputedStyle(row).direction === 'rtl' ? -1 : 1;
      const step = Math.max(row.clientWidth * 0.8, 120) * (event.command === '--pick-next' ? 1 : -1) * rtl;
      row.scrollBy({ left: step, behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
    }
  }

  #showMedia(index) {
    const slides = [...this.querySelectorAll('[data-gallery-slide]')];
    if (slides.length === 0) return;
    const next = (index + slides.length) % slides.length;
    this.#mediaIndex = next;
    slides.forEach((slide, i) => {
      slide.hidden = i !== next;
    });
    this.querySelectorAll('button[data-media-index]').forEach((thumb) => {
      thumb.setAttribute('aria-current', String(Number(thumb.dataset.mediaIndex) === next));
    });
  }

  #formatMoney(cents) {
    const format = this.dataset.moneyFormat || '{{amount}}';
    const value = (cents / 100).toFixed(2);
    const [whole, decimals] = value.split('.');
    const group = (sep) => whole.replace(/\B(?=(\d{3})+(?!\d))/g, sep);
    return format.replace(/\{\{\s*(\w+)\s*\}\}/, (_, kind) => {
      switch (kind) {
        case 'amount_no_decimals': return group(',');
        case 'amount_with_comma_separator': return `${group('.')},${decimals}`;
        case 'amount_no_decimals_with_comma_separator': return group('.');
        case 'amount_with_apostrophe_separator': return `${group("'")}.${decimals}`;
        case 'amount_with_space_separator': return `${group(' ')},${decimals}`;
        default: return `${group(',')}.${decimals}`;
      }
    });
  }

  // Parses a bundle radio's data-variants ("title|id|cents|available;…") into a title → variant map.
  #bundleVariants(option) {
    const map = new Map();
    String(option?.dataset.variants || '').split(';').forEach((entry) => {
      const [title, id, cents, available] = entry.split('|');
      if (title && id) map.set(title, { id, cents: Number(cents) || 0, available: available === 'true' });
    });
    return map;
  }

  #sync() {
    const variant = this.querySelector('[data-variant-option]:checked');
    const bundle = this.querySelector('[data-bundle-option]:checked');
    const idInput = this.querySelector('input[name="id"]');
    const quantityInput = this.querySelector('input[name="quantity"]');
    const quantity = bundle ? Number(bundle.value) || 1 : 1;

    // Always one cart line: the single harness, or the lot product (quantity 1).
    if (quantityInput) quantityInput.value = '1';
    if (!variant) return;
    if (idInput) idInput.value = variant.value;

    const data = variant.dataset;

    // Per-item thumbnail rows: shown for every option (lot 1 = a single row mirroring the model picker);
    // their properties[Article N] inputs are enabled only for lots of 2 or 3.
    const picksWrapper = this.querySelector('[data-bundle-picks]');
    const groups = this.#pickGroups();
    const usePicks = Boolean(picksWrapper) && quantity > 1;
    if (picksWrapper) picksWrapper.hidden = false;
    const chosen = [];
    groups.forEach((group, i) => {
      const active = usePicks && i < quantity;
      group.hidden = !(active || i === 0);
      const checked = group.querySelector('input[type="radio"]:checked');
      const input = group.querySelector('[data-pick-input]');
      if (input) {
        input.disabled = !active || !checked;
        if (checked) input.value = checked.dataset.title;
      }
      const pickLabel = group.querySelector('[data-pick-label]');
      if (pickLabel && checked) pickLabel.textContent = checked.dataset.title;
      if (active) chosen.push(checked);
    });

    const variantCents = Number(data.cents) || 0;
    let available = data.available === 'true';
    // Single item: strike through the variant's compare-at price when it is higher than the price.
    const compareCents = Number(data.compareCents) || 0;
    let baseCents = compareCents > variantCents ? compareCents : variantCents;
    let finalCents = variantCents;

    if (quantity > 1) {
      // Lot: the bundle product's variant for item 1's animal; its price is the lot price.
      const lots = this.#bundleVariants(bundle);
      // Prefer the lot variant matching item 1's animal; if that one isn't sellable, use any sellable lot variant
      // (all lot variants share the same price, and the real animals travel as line properties).
      const wanted = lots.get(chosen[0]?.dataset.title || data.title);
      const lotVariant = (wanted?.available ? wanted : null)
        || [...lots.values()].find((lot) => lot.available)
        || wanted
        || [...lots.values()][0];
      // Reference for a lot: each item's compare-at price when higher than its price, else its price.
      const unitReference = (radio) => {
        const cents = Number(radio?.dataset.cents) || 0;
        const compare = Number(radio?.dataset.compareCents) || 0;
        return compare > cents ? compare : cents;
      };
      baseCents = usePicks
        ? chosen.reduce((sum, radio) => sum + unitReference(radio), 0)
        : baseCents * quantity;
      finalCents = lotVariant ? lotVariant.cents : baseCents;
      available = Boolean(lotVariant?.available) && chosen.every((radio) => radio && !radio.disabled);
      if (idInput && lotVariant) idInput.value = lotVariant.id;
    }

    const price = this.querySelector('[data-buy-price]');
    if (price) price.textContent = this.#formatMoney(finalCents);
    const compare = this.querySelector('[data-buy-compare]');
    if (compare) {
      compare.hidden = finalCents >= baseCents;
      compare.textContent = finalCents < baseCents ? this.#formatMoney(baseCents) : '';
    }

    const status = this.querySelector('[data-buy-status]');
    if (status) {
      // Real low stock only: data-stock is set server-side when the variant tracks inventory and has 1–10 left.
      const stock = Number(data.stock) || 0;
      const lowStock = available && stock > 0 && !usePicks;
      status.textContent = !available
        ? this.dataset.soldOut
        : lowStock
          ? (this.dataset.lowStock || '').replace('99', String(stock))
          : this.dataset.inStock;
      status.closest('[data-buy-status-row]')?.toggleAttribute('data-low', lowStock);
    }

    // Urgency pill: real low stock of the selected model, else the bundle offer.
    const badgeLow = this.querySelector('[data-badge-low]');
    const badgeOffer = this.querySelector('[data-badge-offer]');
    const modelStock = Number(data.stock) || 0;
    const showLowBadge = data.available === 'true' && modelStock > 0;
    if (badgeLow) {
      badgeLow.hidden = !showLowBadge;
      if (showLowBadge) badgeLow.textContent = (this.dataset.badgeLowStock || '').replace('99', String(modelStock));
    }
    if (badgeOffer) badgeOffer.hidden = showLowBadge;
    this.querySelectorAll('[data-badge-dot]').forEach((dot) => {
      dot.classList.toggle('bg-accent', showLowBadge);
      dot.classList.toggle('bg-interactive', !showLowBadge);
    });

    const addButton = this.querySelector('[data-buy-add]');
    if (addButton) addButton.toggleAttribute('disabled', !available);

    const label = this.querySelector('[data-buy-variant-label]');
    if (label) label.textContent = data.title;

    // Bundle rows show the current model's price for that quantity.
    this.querySelectorAll('[data-bundle-total]').forEach((element) => {
      const qty = Number(element.dataset.bundleTotal) || 1;
      const option = this.querySelector(`[data-bundle-option][value="${qty}"]`);
      const lotMap = qty > 1 ? this.#bundleVariants(option) : null;
      const lot = lotMap ? (lotMap.get(data.title) || [...lotMap.values()][0]) : null;
      element.textContent = this.#formatMoney(lot ? lot.cents : variantCents * qty);
      const compareEl = this.querySelector(`[data-bundle-compare="${qty}"]`);
      if (compareEl) {
        const referenceCents = (compareCents > variantCents ? compareCents : variantCents) * qty;
        compareEl.textContent = this.#formatMoney(referenceCents);
        const rowCents = lot ? lot.cents : variantCents * qty;
        compareEl.hidden = (qty > 1 && !lot) || rowCents >= referenceCents;
      }
    });

    if (data.mediaIndex !== undefined && data.mediaIndex !== '') this.#showMedia(Number(data.mediaIndex));
  }
}

if (!customElements.get('s-buy-box')) customElements.define('s-buy-box', SBuyBox);
